import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { useAutosave } from '../../lib/useAutosave'
import { moderateLyrics } from '../../lib/moderate'
import { reachOpensTo } from '../../lib/reach'
import { toSentenceCase } from '../../lib/format'
import { chordAt, normalizeKey, putChord, removeChord, transposeKey } from '../../lib/chords'
import { parseSheetFile } from '../../lib/sheetImport'
import { ocrImage } from '../../lib/ocr'
import { extractPdfText, rasterizeFirstPage } from '../../lib/pdfImport'
import { Shell } from '../../components/Shell'
import { Button, Field, MiniSpin, Progress, Saved, Spinner, TrustRow, Check, Player, initials }
  from '../../components/ui'
import MediaUpload from './MediaUpload'
import ScripturePicker from '../../components/ScripturePicker'
import SignatureGate from '../../components/SignatureGate'
import ChordChart from '../../components/ChordChart'
import ChordPicker from '../../components/ChordPicker'
import InvitePanel from '../../components/InvitePanel'

const TOTAL = 5

/** Grows a textarea to fit what's actually in it — an imported section can
 * carry eight lines, and a fixed-height box just meant scrolling inside a
 * scrolling page to read your own lyrics back. Resets to 'auto' first so a
 * deletion shrinks the box instead of it only ever growing. */
const autoGrow = (el) => {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

const SECTION_TYPES = [
  { t: 'verse', label: 'Verse' },
  { t: 'chorus', label: 'Chorus' },
  { t: 'bridge', label: 'Bridge' },
  { t: 'bridge_2', label: 'Bridge 2' },
  { t: 'tag', label: 'Tag' },
]

export default function Submit() {
  const { id, step } = useParams()
  const nav = useNavigate()
  const { user } = useSession()

  const [song, setSong] = useState(null)
  const [ready, setReady] = useState(false)
  const [cowriters, setCowriters] = useState([])
  const [sections, setSections] = useState([])
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s, error }, { data: cw }, { data: sec }] = await Promise.all([
      supabase.from('songs').select('*').eq('id', id).maybeSingle(),
      supabase.from('song_cowriters').select('*').eq('song_id', id).order('is_primary', { ascending: false }),
      supabase.from('song_sections').select('*').eq('song_id', id).order('order_position'),
    ])
    if (error) setErr(readableError(error))
    setSong(s ?? null)
    setCowriters(cw ?? [])
    setSections(sec ?? [])
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell hidePlayer><Spinner /></Shell>
  if (!song) return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <h2 className="q">We can't find that song.</h2>
      <p className="sub">It may have been removed, or it isn't yours to open.</p>
    </Shell>
  )
  if (song.songwriter_id !== user.id) return (
    <Shell hidePlayer back="/songs" title="Your songs"><p className="sub">This isn't your song.</p></Shell>
  )

  const go = (n) => nav(`/songs/${id}/${n}`)
  const common = { song, setSong, cowriters, setCowriters, sections, setSections, reload: load, go, err, setErr }

  if (step === 'done') return <Done {...common} />
  const n = Math.min(Math.max(parseInt(step, 10) || 1, 1), TOTAL)
  const Step = [W1, W2, W3, W4, W5][n - 1]
  return <Step {...common} n={n} />
}

/* ------------------------------------------------------------------ W1 */
function W1({ song, setSong, go, n }) {
  const [f, setF] = useState({
    title: song.title === 'Untitled' ? '' : song.title || '',
    season: song.season_note || '',
  })

  const saveState = useAutosave(f, async (v) => {
    const patch = {
      title: v.title.trim() ? toSentenceCase(v.title) : 'Untitled',
      season_note: v.season.trim() || null,
    }
    const { error } = await supabase.from('songs').update(patch).eq('id', song.id)
    if (error) throw error
    setSong({ ...song, ...patch })
  })

  // Scripture saves on confirmation, not on keystroke — the writer has said
  // "yes, that's it" against real text.
  const saveScripture = async ({ reference, translation, text, verses }) => {
    const patch = {
      scripture_refs: [reference],
      scripture_translation: translation,
      scripture_text: text,
      scripture_verses: verses,
    }
    const { error } = await supabase.from('songs').update(patch).eq('id', song.id)
    if (!error) setSong({ ...song, ...patch })
  }

  return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">New song</div>
      <h2 className="q">Let's start with the song.</h2>
      <p className="sub">Only you see this until it's ready. Everything saves as you type.</p>

      {song.song_number && (
        <p className="small" style={{ marginBottom: 16, fontWeight: 'bold' }}>
          Ref: {song.song_number}
        </p>
      )}

      {/* Two groups, not four evenly-spaced questions. The title and its
          scripture are the song as anyone else will meet it; the season note is
          private and belongs to the writer. Equal gaps between all four made
          that distinction something the caption had to carry alone. */}
      <div className="fieldgroup">
        <Field label="Title">
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </Field>
        <ScripturePicker
          value={(song.scripture_refs || [])[0] || ''}
          translation={song.scripture_translation}
          text={song.scripture_text}
          verses={song.scripture_verses}
          onChange={saveScripture}
        />
      </div>

      <div className="fieldgroup">
        <Field label="What season did it come out of?">
          <textarea className="ta" value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })}
                    placeholder="Not shared. Just for you to notice as you bring it in." />
        </Field>
        <p className="small">
          The season note stays yours. Nobody at the church ever sees it.
        </p>
      </div>

      <Saved state={saveState} />
      <div className="hr" />
      <button className="btn btn-primary" disabled={!f.title.trim()} onClick={() => go(2)}>
        Next — the words
      </button>
    </Shell>
  )
}

/* ------------------------------------------------------------------ W2 */
function W2({ song, setSong, sections, setSections, go, n }) {
  const [err, setErr] = useState(null)
  // Which face each section is showing. Words is the default: you cannot put a
  // chord over a word that isn't written yet.
  const [face, setFace] = useState({})
  const [picking, setPicking] = useState(null) // { sectionId, l, w }
  const [mode, setMode] = useState(
    () => localStorage.getItem('beseter.chart.mode') || 'chords'
  )
  // This step wrote lyrics on blur and chords on a debounce and said nothing
  // about either. W1 promises "everything saves as you type" one screen earlier,
  // so the writer has been told to expect an answer here and wasn't getting one.
  const [saveState, setSaveState] = useState('idle')
  const saveTimer = useRef(null)

  const songKey = normalizeKey(song.key) ?? 'C'

  useEffect(() => { localStorage.setItem('beseter.chart.mode', mode) }, [mode])
  useEffect(() => () => clearTimeout(saveTimer.current), [])

  const addSection = async (type) => {
    const num = sections.filter((s) => s.section_type === type).length + 1
    const { data, error } = await supabase.from('song_sections').insert({
      song_id: song.id, section_type: type, section_number: num,
      lyrics: '', order_position: sections.length + 1,
    }).select().single()
    if (error) return setErr(readableError(error))
    setSections([...sections, data])
  }

  const [importing, setImporting] = useState(false)
  const fileInput = useRef(null)
  // A photo or a PDF both go through extraction before anything is trustworthy
  // — OCR guesses at letters, and a PDF's text layer can come back in the
  // wrong reading order for a multi-column chart. Either way it lands here for
  // a look before insertion, rather than committing straight to song_sections
  // the way a plain text file does.
  const [reviewText, setReviewText] = useState(null)
  const [reviewBusy, setReviewBusy] = useState(false)
  const [reviewProgress, setReviewProgress] = useState(0)

  // Bulk insert bypasses addSection's one-row-at-a-time flow deliberately —
  // pasting in a 5-section sheet should not fire 5 sequential round trips.
  const commitParsedText = async (text) => {
    const parsed = parseSheetFile(text, songKey, song.title)
    if (parsed.length === 0) {
      setErr("Couldn't find any sections in that.")
      return false
    }
    const counters = {}
    for (const s of sections) counters[s.section_type] = Math.max(counters[s.section_type] ?? 0, s.section_number)
    const rows = parsed.map((s, i) => {
      const num = (counters[s.section_type] = (counters[s.section_type] ?? 0) + 1)
      return {
        song_id: song.id, section_type: s.section_type, section_number: num,
        lyrics: s.lyrics, chords: s.chords, order_position: sections.length + i + 1,
      }
    })
    const { data, error } = await supabase.from('song_sections').insert(rows).select()
    if (error) { setErr(readableError(error)); return false }
    setSections([...sections, ...data])
    return true
  }

  // One button, one input, dispatched by what actually got picked — a text
  // file commits straight away, a PDF or a photo goes through extraction and
  // lands in the review step first.
  const importFile = async (file) => {
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
    // iOS sometimes hands back an empty `type` for a file picked via Browse
    // rather than Photos — the extension is the fallback, same reasoning as
    // the PDF check above.
    const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|gif|webp)$/i.test(file.name)
    if (isPdf) return importPdf(file)
    if (isImage) return importPhoto(file)

    setImporting(true)
    setErr(null)
    try {
      await commitParsedText(await file.text())
    } finally {
      setImporting(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const importPdf = async (file) => {
    setReviewBusy(true)
    setReviewProgress(0)
    setErr(null)
    try {
      const text = await extractPdfText(file)
      // A real text layer usually runs to hundreds of characters even for one
      // section. A near-empty result means a scanned page with nothing to
      // pull, so fall back to OCR on the rendered page rather than reporting
      // failure on a PDF that's perfectly readable to a person.
      if (text.trim().length > 40) {
        setReviewText(text)
      } else {
        const blob = await rasterizeFirstPage(file)
        setReviewText(await ocrImage(blob, setReviewProgress))
      }
    } catch {
      setErr("Couldn't read that PDF — try exporting it as text, or a screenshot instead.")
    } finally {
      setReviewBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const importPhoto = async (file) => {
    setReviewBusy(true)
    setReviewProgress(0)
    setErr(null)
    try {
      setReviewText(await ocrImage(file, setReviewProgress))
    } catch {
      setErr("Couldn't read that photo — try a clearer screenshot, or paste the text instead.")
    } finally {
      setReviewBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const confirmReviewImport = async () => {
    setImporting(true)
    try {
      if (await commitParsedText(reviewText)) setReviewText(null)
    } finally {
      setImporting(false)
    }
  }

  const write = async (sec, lyrics) => {
    if (lyrics === sec.lyrics) return // blur without an edit is not a save
    setSections(sections.map((s) => (s.id === sec.id ? { ...s, lyrics } : s)))
    setSaveState('saving')
    const { error } = await supabase.from('song_sections').update({ lyrics }).eq('id', sec.id)
    setSaveState(error ? 'error' : 'saved')
  }

  /**
   * Chords land in local state at once and reach the database on a delay. Every
   * notch of the quality wheel is a change, and writing each one would mean a
   * request per flick — the wheel is a continuous control feeding a discrete
   * store, so the debounce is doing real work rather than being cautious.
   */
  const putChords = (sec, chords) => {
    setSections((prev) => prev.map((s) => (s.id === sec.id ? { ...s, chords } : s)))
    setSaveState('saving')
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      const { error } = await supabase.from('song_sections')
        .update({ chords }).eq('id', sec.id)
      setSaveState(error ? 'error' : 'saved')
      if (error) setErr(readableError(error))
    }, 400)
  }

  const setKey = async (k) => {
    setSong({ ...song, key: k })
    setSaveState('saving')
    const { error } = await supabase.from('songs').update({ key: k }).eq('id', song.id)
    setSaveState(error ? 'error' : 'saved')
  }

  const drop = async (sec) => {
    await supabase.from('song_sections').delete().eq('id', sec.id)
    setSections(sections.filter((s) => s.id !== sec.id))
    if (picking?.sectionId === sec.id) setPicking(null)
  }

  const title = (s) => {
    const base = SECTION_TYPES.find((x) => x.t === s.section_type)?.label ?? s.section_type
    return s.section_type === 'verse' ? `${base} ${s.section_number}` : base
  }

  const activeSection = picking && sections.find((s) => s.id === picking.sectionId)
  const activeChord = activeSection
    ? chordAt(activeSection.chords, picking.l, picking.w)
    : null

  return (
    <Shell hidePlayer back={`/songs/${song.id}/1`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">{song.title}</div>
      <h2 className="q">The words.</h2>
      <p className="sub">
        Add sections as you go. A rough voice memo is plenty — and once there are
        words, you can lay chords over them.
      </p>

      {/* Bridge 2 is not a section you reach for; it's a section you reach for
          second. Offering all five at once puts a choice in front of a writer on
          an empty screen that cannot mean anything yet, and the cost of a menu
          is paid per option whether or not the option is usable. It appears the
          moment there is a bridge to be second to. */}
      <div className="chips" style={{ marginBottom: 16 }}>
        {SECTION_TYPES
          .filter((s) => s.t !== 'bridge_2' || sections.some((x) => x.section_type === 'bridge'))
          .map((s) => (
            <button key={s.t} className="chip" onClick={() => addSection(s.t)}>+ {s.label}</button>
          ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        {/* No `accept` filter. iOS Safari has a real bug where combining
            `image/*` with specific extensions/MIME types in one `accept`
            list narrows the Files/Browse view down to images only, hiding
            PDFs from it entirely — the picker's own filtering, not this
            code's. The routing below already inspects the actual file after
            selection, so nothing here depends on `accept` to work. */}
        <input ref={fileInput} type="file" hidden
               onChange={(e) => e.target.files[0] && importFile(e.target.files[0])} />
        <button className="btn btn-quiet" disabled={importing || reviewBusy}
                onClick={() => fileInput.current?.click()}>
          {reviewBusy ? `Reading…${reviewProgress ? ` ${Math.round(reviewProgress * 100)}%` : ''}`
            : importing ? 'Importing…' : 'Import'}
        </button>
        <p className="small" style={{ margin: '6px 0 0' }}>
          A text file, a PDF, or a screenshot — a Note, a photo of a printed
          chart. Section labels like "Verse 1" or "[Chorus]" keep it
          organised. Chords work either written above the lyric line or
          inline as [G]like this.
        </p>
      </div>

      {reviewText !== null && (
        <div className="tile" style={{ marginBottom: 16 }}>
          <p className="small" style={{ margin: '0 0 6px', fontWeight: 600 }}>
            Here's what came out of that. Fix anything before adding it.
          </p>
          <textarea className="ta" style={{ minHeight: 180, resize: 'none', overflow: 'hidden' }}
                    value={reviewText} ref={autoGrow}
                    onChange={(e) => { setReviewText(e.target.value); autoGrow(e.target) }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" disabled={importing || !reviewText.trim()}
                    onClick={confirmReviewImport}>
              {importing ? 'Adding…' : 'Looks right — add these sections'}
            </button>
            <button className="btn btn-quiet" disabled={importing} onClick={() => setReviewText(null)}>
              Discard
            </button>
          </div>
        </div>
      )}

      {sections.map((s) => {
        const showChords = face[s.id] === 'chords'
        const hasWords = (s.lyrics ?? '').trim().length > 0
        return (
          <div className="field" key={s.id}>
            <label className="label" style={{ display: 'flex', justifyContent: 'space-between',
                                              alignItems: 'center', gap: 8 }}>
              {title(s)}
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="seg seg-sm" role="group" aria-label={`${title(s)} view`}>
                  <button className={`seg-b${!showChords ? ' on' : ''}`}
                          onClick={() => { setFace({ ...face, [s.id]: 'words' }); setPicking(null) }}>
                    Words
                  </button>
                  <button className={`seg-b${showChords ? ' on' : ''}`} disabled={!hasWords}
                          title={hasWords ? undefined : 'Write some words first'}
                          onClick={() => setFace({ ...face, [s.id]: 'chords' })}>
                    Chords
                  </button>
                </span>
                <button className="btn-plain" style={{ minHeight: 0, padding: 0 }}
                        onClick={() => drop(s)}>remove</button>
              </span>
            </label>

            {showChords ? (
              <div className="chartedit">
                <p className="small" style={{ margin: '0 0 6px' }}>
                  Tap a word to put a chord on it.
                </p>
                <ChordChart
                  sections={[s]}
                  mode={mode}
                  songKey={songKey}
                  active={picking}
                  onPick={(sec, l, w) => setPicking(
                    picking && picking.sectionId === sec.id && picking.l === l && picking.w === w
                      ? null
                      : { sectionId: sec.id, l, w }
                  )}
                />
              </div>
            ) : (
              <textarea className="ta" style={{ resize: 'none', overflow: 'hidden' }}
                        defaultValue={s.lyrics} ref={autoGrow}
                        onInput={(e) => autoGrow(e.target)}
                        onBlur={(e) => write(s, e.target.value)} />
            )}
          </div>
        )
      })}

      {sections.length === 0 && <p className="small">No words yet — that's allowed if your church takes voice memos.</p>}

      {/* The picker is one panel for the whole step, anchored under the section
          being edited rather than floating over it. A modal here would cover the
          line you are trying to put the chord on. */}
      {activeSection && (
        <>
          <div className="chartbar-edit">
            <div className="seg" role="group" aria-label="Chart notation">
              <button className={`seg-b${mode === 'chords' ? ' on' : ''}`}
                      onClick={() => setMode('chords')}>Chords</button>
              <button className={`seg-b${mode === 'numbers' ? ' on' : ''}`}
                      onClick={() => setMode('numbers')}>Numbers</button>
            </div>
            {/* Same transpose control as the print sheet — one stepper
                vocabulary for "change the key" everywhere it appears, rather
                than a dropdown here and a stepper there. Unlike the sheet's
                version this one writes straight to song.key: there's no
                separate "preview a different key" concept during writing. */}
            <div className="transpose">
              <button className="iconbtn" onClick={() => setKey(transposeKey(songKey, -1))}
                      aria-label="Down a semitone">−</button>
              <span className="transpose-k">{songKey}</span>
              <button className="iconbtn" onClick={() => setKey(transposeKey(songKey, 1))}
                      aria-label="Up a semitone">+</button>
            </div>
          </div>
          {!song.key && (
            <p className="small" style={{ margin: '0 0 10px' }}>
              No key set yet, so this is showing in C. Set it above and the letters
              follow — the chords themselves are stored as numbers either way.
            </p>
          )}
          <ChordPicker
            value={activeChord}
            songKey={songKey}
            mode={mode}
            onChange={(ch) => putChords(
              activeSection,
              putChord(activeSection.chords, { ...ch, l: picking.l, w: picking.w })
            )}
            onRemove={activeChord ? () => {
              putChords(activeSection, removeChord(activeSection.chords, picking.l, picking.w))
              setPicking(null)
            } : null}
            onClose={() => setPicking(null)}
          />
        </>
      )}

      <div style={{ marginTop: 18 }}>
        <MediaUpload song={song} onChange={(patch) => setSong({ ...song, ...patch })} />
      </div>

      {sections.length > 0 && <Saved state={saveState} />}
      {err && <p className="err">{err}</p>}
      <div className="hr" />
      {/*
        Moderation runs on leaving this step, not on every edit. The textareas
        save onBlur, so a writer reworking a chorus would otherwise spend a
        dozen model calls on drafts of a line they are still writing — and the
        daily cap would be gone before the song was finished.
      */}
      <button className="btn btn-primary"
              onClick={() => { moderateLyrics(song.id); go(3) }}>
        Next — who wrote it
      </button>
    </Shell>
  )
}

/* ------------------------------------------------------------------ W3 */
function W3({ song, cowriters, setCowriters, reload, go, n }) {
  const [err, setErr] = useState(null)
  const [adding, setAdding] = useState(null) // { name, email }
  const [busy, setBusy] = useState(false)
  const [saveState, setSaveState] = useState('idle')
  const total = cowriters.reduce((a, c) => a + Number(c.split_percentage || 0), 0)
  const ok = total === 100

  const setPct = (row, value) => {
    const v = value === '' ? 0 : Math.max(0, Math.min(100, Number(value)))
    setCowriters(cowriters.map((c) => (c.id === row.id ? { ...c, split_percentage: v } : c)))
  }

  const persist = async (row) => {
    // split_percentage has a > 0 check in the schema; hold the write until it's valid.
    if (!(row.split_percentage > 0)) return
    setSaveState('saving')
    const { error } = await supabase.from('song_cowriters')
      .update({ split_percentage: row.split_percentage }).eq('id', row.id)
    setSaveState(error ? 'error' : 'saved')
    if (error) { setErr(readableError(error)); reload() }
  }

  /**
   * Adding a writer splits the song evenly between everyone on it.
   *
   * The row used to land on 1% — a placeholder the schema would accept — which
   * left the writer staring at 101% and doing the arithmetic for a number the
   * app already knew. An even split is what most songs settle on and all of it
   * is still editable; the default is a starting point, not a decision made for
   * them. Any indivisible remainder goes to the primary writer rather than
   * being scattered, so the numbers stay round.
   *
   * A signed share can't be moved, so once anyone has signed we fall back to the
   * old placeholder rather than silently failing against the guard trigger.
   */
  const add = async () => {
    setBusy(true); setErr(null)
    const locked = cowriters.some((c) => c.signed)
    const n = cowriters.length + 1
    const share = locked ? 1 : Math.floor(100 / n)
    const remainder = locked ? 0 : 100 - share * n

    const { error } = await supabase.from('song_cowriters').insert({
      song_id: song.id,
      cowriter_name: toSentenceCase(adding.name),
      cowriter_email: adding.email.trim() ? adding.email.trim().toLowerCase() : null,
      split_percentage: share,
    })
    if (!error && !locked) {
      await Promise.all(cowriters.map((c) => supabase.from('song_cowriters')
        .update({ split_percentage: share + (c.is_primary ? remainder : 0) })
        .eq('id', c.id)))
    }
    setBusy(false)
    if (error) return setErr(readableError(error))
    setAdding(null)
    reload()
  }

  const remove = async (row) => {
    const { error } = await supabase.from('song_cowriters').delete().eq('id', row.id)
    if (error) return setErr(readableError(error))
    reload()
  }

  const label = ok ? 'Next — the declarations'
    : total > 100 ? `Over by ${total - 100}%` : `${100 - total}% still to allocate`

  return (
    <Shell hidePlayer back={`/songs/${song.id}/2`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Who wrote it</div>
      <h2 className="q">Sort the splits.</h2>
      <p className="sub">Agree the shares now, while it's easy. It has to add up to exactly 100 before it can move.</p>

      {cowriters.map((c) => (
        <div className="split" key={c.id}>
          <div className="ava">{initials(c.cowriter_name)}</div>
          <div className="nm">
            {c.cowriter_name}
            <small>
              {c.is_primary ? 'You' : c.signed ? 'Co-writer · signed' : 'Co-writer · not yet signed'}
            </small>
          </div>
          <div className="pctwrap">
            <input className="pct" inputMode="numeric" value={c.split_percentage}
                   disabled={c.signed}
                   onChange={(e) => setPct(c, e.target.value)}
                   onBlur={() => persist(cowriters.find((x) => x.id === c.id))} />
            <span className="small">%</span>
            {!c.is_primary && !c.signed && (
              <button className="btn-plain" style={{ minHeight: 0, padding: '0 0 0 6px' }}
                      onClick={() => remove(c)}>×</button>
            )}
          </div>
        </div>
      ))}

      {/* The running total sits against the rows it adds up, not below the
          "add a co-writer" control — a number separated from its column by an
          unrelated action stops reading as that column's total. */}
      <div className="splittotal">
        <span className="small">Total allocated</span>
        <span className={`amt ${ok ? 'ok' : 'bad'}`}>{total}%</span>
      </div>
      {cowriters.some((c) => c.signed) && (
        <p className="small" style={{ marginTop: 8 }}>
          A share can't be moved once that writer has signed it.
        </p>
      )}
      <Saved state={saveState} />

      <div className="hr" />

      {adding ? (
        <div style={{ paddingBottom: '4px' }}>
          <Field label="Co-writer's name">
            <input className="input" autoFocus value={adding.name}
                   onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
          </Field>
          <Field label="Their email (optional)">
            <input className="input" type="email" value={adding.email}
                   onChange={(e) => setAdding({ ...adding, email: e.target.value })} />
          </Field>
          <p className="small" style={{ marginTop: -8, marginBottom: 12 }}>
            With an email, we can send them a sign-in link. No email? Add them by
            name and share a code with them yourself instead — either way, a
            signature always ties to a real account. {!cowriters.some((c) => c.signed) &&
              `Adding them splits the song ${Math.floor(100 / (cowriters.length + 1))}% each; you can change that after.`}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button busy={busy}
                    disabled={!adding.name.trim() || (adding.email.trim() && !adding.email.includes('@'))}
                    onClick={add}>
              Add them
            </Button>
            <Button className="btn btn-quiet" disabled={busy}
                    onClick={() => setAdding(null)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button className="btn-plain" style={{ paddingLeft: 0 }}
                onClick={() => setAdding({ name: '', email: '' })}>
          + Add a co-writer
        </button>
      )}

      {err && <p className="err">{err}</p>}
      <div className="hr" />
      <button className="btn btn-primary" disabled={!ok} onClick={() => go(4)}>{label}</button>
    </Shell>
  )
}

const CONTACT_EMAIL = 'hello@secretplacemusic.au'

/* ------------------------------------------------------------------ W4 */
function W4({ song, setSong, go, n }) {
  const [d, setD] = useState({
    decl_original: song.decl_original,
    decl_ownership: song.decl_ownership,
    decl_free_to_share: song.decl_free_to_share,
  })
  // Separate from decl_free_to_share, which is a plain boolean with no way to
  // tell "hasn't answered" from "explicitly declined". This is what actually
  // gates the wizard — decl_free_to_share only records the happy path.
  const [thirdParty, setThirdParty] = useState(song.third_party_published ?? null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const all = d.decl_original && d.decl_ownership && d.decl_free_to_share

  const answerThirdParty = async (published) => {
    setBusy(true); setErr(null)
    const patch = published
      ? { third_party_published: true, decl_free_to_share: false }
      : { third_party_published: false, decl_free_to_share: true }
    const { error } = await supabase.from('songs').update(patch).eq('id', song.id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    setThirdParty(patch.third_party_published)
    setD({ ...d, decl_free_to_share: patch.decl_free_to_share })
    setSong({ ...song, ...patch })
  }

  const confirm = async () => {
    const patch = { ...d, declarations_confirmed_at: new Date().toISOString() }
    const { error } = await supabase.from('songs').update(patch).eq('id', song.id)
    if (error) return setErr(readableError(error))
    setSong({ ...song, ...patch })
    go(5)
  }

  const items = [
    ['decl_original', 'This is original work', "Not built on someone else's copyrighted song"],
    ['decl_ownership', 'The splits above are agreed', 'Everyone named has agreed their share'],
  ]

  return (
    <Shell hidePlayer back={`/songs/${song.id}/3`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Before it moves</div>
      <h2 className="q">A few things to confirm.</h2>
      <p className="sub">These become the ticks your pastor sees. Tap each to confirm.</p>

      <div className="stack">
        {items.map(([k, title, hint]) => (
          <button key={k} className={`chip chip-wide${d[k] ? ' on' : ''}`}
                  onClick={() => setD({ ...d, [k]: !d[k] })}>
            <span style={{ flex: 1 }}>{title}<small>{hint}</small></span>
            {d[k] ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <p className="small" style={{ marginTop: 18, fontWeight: 600 }}>
        Is this song already published or administered by anyone other than you?
      </p>
      <div className="stack" style={{ marginTop: 6 }}>
        <button className={`chip chip-wide${thirdParty === false ? ' on' : ''}`}
                disabled={busy} onClick={() => answerThirdParty(false)}>
          <span style={{ flex: 1 }}>No — I'm free to share it
            <small>Not signed to another publisher for this song</small>
          </span>
          {thirdParty === false ? <Check /> : '○'}
        </button>
        <button className={`chip chip-wide${thirdParty === true ? ' on' : ''}`}
                disabled={busy} onClick={() => answerThirdParty(true)}>
          <span style={{ flex: 1 }}>Yes, it's already published or administered elsewhere
            <small>By a publisher, label, or another PRO-registered administrator</small>
          </span>
          {thirdParty === true ? <Check /> : '○'}
        </button>
      </div>

      {thirdParty === true && (
        <div className="tile" style={{ marginTop: 14 }}>
          <p style={{ margin: 0 }}>It sounds like your song is already in safe hands.</p>
          <p className="small" style={{ marginTop: 6 }}>
            Beseter can't administer or publish songs covered under an existing
            exclusive agreement. If you'd like to talk it through, Secret Place
            Music is one email away.
          </p>
          <a className="btn btn-primary" style={{ marginTop: 10 }}
             href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Already published: ${song.title || 'Untitled'}`)}`}>
            Contact Secret Place Music
          </a>
        </div>
      )}

      <p className="small" style={{ marginTop: 14 }}>
        Timestamped when you confirm. This is your record if anyone ever asks who wrote it first.
      </p>
      {song.declarations_confirmed_at && (
        <p className="small">Confirmed {new Date(song.declarations_confirmed_at).toLocaleString()}.</p>
      )}

      {err && <p className="err">{err}</p>}
      <div className="hr" />
      <button className="btn btn-primary" disabled={!all} onClick={confirm}>Next — co-sign</button>
    </Shell>
  )
}

/* ------------------------------------------------------------------ W5 */
function W5({ song, cowriters, reload, go, n }) {
  const { user } = useSession()
  const [busy, setBusy] = useState(false)
  // Which invite is in flight. An email round-trip through an edge function is
  // the longest wait in the flow, and the button was giving no sign it had been
  // pressed — so the second press was inevitable, and it sent a second email.
  const [inviting, setInviting] = useState(null)
  const [err, setErr] = useState(null)
  // Which cowriter's share panel is open, and the invite_codes row behind it —
  // fetched once per cowriter and kept rather than re-created on every open,
  // since a cowriter code is single-use and reopening shouldn't mint a new one.
  const [codeFor, setCodeFor] = useState(null)
  const [codes, setCodes] = useState({})

  // The church lookup that used to live here is gone with the dropdown. When
  // the sharing screen arrives it belongs there, alongside circles and the
  // collective — not welded to the end of the writing flow.

  const total = cowriters.reduce((a, c) => a + Number(c.split_percentage || 0), 0)
  const allSigned = cowriters.length > 0 && cowriters.every((c) => c.signed)
  const gateOpen = allSigned && total === 100
  const mine = cowriters.find((c) => c.user_id === user.id)

  const invite = async (c) => {
    setErr(null); setInviting(c.id)
    const { error } = await supabase.functions.invoke('send-cowriter-invite', {
      body: { cowriter_id: c.id },
    })
    setInviting(null)
    if (error) return setErr('Invite email failed to send — check the Resend secret.')
    reload()
  }

  const shareCode = async (c) => {
    if (codeFor === c.id) return setCodeFor(null)
    setErr(null)
    if (!codes[c.id]) {
      const { data, error } = await supabase.rpc('create_invite_code',
        { p_kind: 'cowriter', p_song_cowriter_id: c.id })
      if (error) return setErr(readableError(error))
      setCodes((prev) => ({ ...prev, [c.id]: data }))
    }
    setCodeFor(c.id)
  }

  /**
   * Finishing is not sending.
   *
   * This used to call submit_song, which demands an approved church membership
   * — and the dropdown above it only listed churches that had already approved
   * the writer. A new writer saw an empty list and a button that would never
   * enable, one click from the end of the flow.
   *
   * A church verifies a writer and endorses their songs. It was never meant to
   * be the door they have to get through before they can finish one. Where the
   * song goes afterwards — an inner circle, the collective, a church — is a
   * separate decision, made later, on a screen that does not exist yet.
   */
  const send = async () => {
    setBusy(true); setErr(null)
    const { error } = await supabase.rpc('finish_song', { p_song: song.id })
    setBusy(false)
    if (error) return setErr(readableError(error))
    go('done')
  }

  return (
    <Shell hidePlayer back={`/songs/${song.id}/4`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">The gate</div>
      <h2 className="q">Everyone signs, then it's done.</h2>
      <p className="sub">
        A song isn't finished until every co-writer has signed off and the splits
        add up. This protects all of you — and it's settled once, here, rather
        than argued about later.
      </p>

      {cowriters.map((c) => (
        <div key={c.id}>
          <div className={`cosign${c.signed ? ' signed' : ''}`}>
            <div className="ava">{initials(c.cowriter_name)}</div>
            <div className="who">
              <div className="n">{c.cowriter_name}</div>
              <div className="st">
                {c.signed
                  ? `Signed · ${new Date(c.signed_at).toLocaleDateString()}`
                  : c.invite_sent_at ? 'Invited · waiting on signature' : 'Waiting on signature'}
                {' · '}{c.split_percentage}%
              </div>
            </div>
            {c.signed
              ? <span className="badge">Signed</span>
              : c.user_id === user.id
                ? <span className="badge">You</span>
                : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {c.cowriter_email && (
                      <button className="iconbtn" onClick={() => invite(c)}
                              disabled={inviting === c.id} aria-busy={inviting === c.id}>
                        {inviting === c.id && <MiniSpin />}
                        {inviting === c.id ? 'Sending' : c.invite_sent_at ? 'Remind' : 'Invite'}
                      </button>
                    )}
                    <button className="iconbtn" onClick={() => shareCode(c)}>
                      {codeFor === c.id ? 'Hide' : 'Share code'}
                    </button>
                  </div>
                )}
          </div>
          {codeFor === c.id && codes[c.id] && (
            <InvitePanel code={codes[c.id].code} kind="cowriter" label={song.title} />
          )}
        </div>
      ))}

      {mine && !mine.signed && (
        <SignatureGate cowriter={mine} songTitle={song.title} onSigned={reload} />
      )}

      <div className="hr" />

      {/* The label no longer has to double as the progress indicator, so it can
          keep saying what the button does while it does it. */}
      <Button busy={busy} disabled={!gateOpen} onClick={send}>
        {gateOpen ? 'Submit song'
          : total !== 100 ? `Splits are at ${total}%`
          : 'Waiting on signatures'}
      </Button>
      <p className="small" style={{ marginTop: 12, textAlign: 'center' }}>
        This finishes the song and takes it out of draft. Who hears it is the
        next question, and it's yours — a separate choice you can remake any time.
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}

/* ---------------------------------------------------------------- done */
function Done({ song, cowriters }) {
  const nav = useNavigate()
  const [src, setSrc] = useState(null)
  const total = cowriters.reduce((a, c) => a + Number(c.split_percentage || 0), 0)
  const names = cowriters.map((c) => c.cowriter_name).join(' · ')
  const splits = cowriters.map((c) => c.split_percentage).join(' / ')

  useEffect(() => {
    if (!song.r2_object_key) return
    supabase.functions.invoke('r2-playback-url', { body: { song_id: song.id } })
      .then(({ data }) => setSrc(data?.url ?? null))
  }, [song.id, song.r2_object_key])

  const decided = !!song.decision
  const DECISION = {
    closer_look: ['A closer look', 'It moves toward a Sunday test — the first yes.'],
    paired: ['Paired up', 'It goes back with a more experienced co-writer to develop.'],
    rework: ['Back for a rework', 'Another pass, then you can send it again.'],
  }

  return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <div className="done-ic"><Check size={26} w={2.4} /></div>
      {/* Not "it's with your church" any more — finishing sends it nowhere. */}
      <h2 className="q center">
        {decided ? DECISION[song.decision][0]
          : song.submitted_at ? "It's with your church."
          : "That's the song done."}
      </h2>
      <p className="sub center">
        {decided
          ? DECISION[song.decision][1]
          : 'Signed by every writer, splits sorted, declarations made. That’s the last of the paperwork for now.'}
      </p>

      {song.decision_note && (
        <div className="trust" style={{ background: 'var(--raised)' }}>
          <p style={{ fontFamily: "'Space Grotesk'", fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            “{song.decision_note}”
          </p>
        </div>
      )}

      {song.r2_object_key && (
        <Player src={src} mime={song.media_mime} duration={song.duration_seconds}
                filename={song.media_filename} />
      )}

      <div className="trust">
        <TrustRow title={`${cowriters.length} writer${cowriters.length === 1 ? '' : 's'} signed`} hint={names} />
        <TrustRow title={`Splits total ${total}%`} hint={splits} />
        <TrustRow title="Declarations confirmed"
          hint={song.declarations_confirmed_at
            ? new Date(song.declarations_confirmed_at).toLocaleString() : '—'} />
      </div>

      <p className="small center">
        We won't chase you, and we won't chase them. If it's a no, it comes back gently.
      </p>
      <div className="hr" />

      {/* Finishing sends it nowhere — that was the whole point of splitting the
          two. So the obvious next thing is offered here rather than bolted onto
          the end of the wizard: the flow reads as continuous, but the song is
          genuinely done and sharing stays a decision the writer can remake. */}
      <button className="btn btn-primary" onClick={() => nav(`/songs/${song.id}/share`)}>
        {song.reach && song.reach !== 'private' ? 'Change who hears it' : 'Choose who hears it'}
      </button>
      <p className="small center" style={{ margin: '10px 0 18px' }}>
        {song.reach && song.reach !== 'private'
          ? <>Open to <b>{reachOpensTo(song.reach).join(' · ')}</b>.</>
          : 'It stays yours until you say otherwise.'}
      </p>
      <button className="btn btn-quiet" onClick={() => nav('/me')}>Back to your songs</button>
    </Shell>
  )
}
